import React from 'react';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import { useMutation } from '@tanstack/react-query';
import { useToggle } from '@uidotdev/usehooks';

import { AuthTemplate } from '@components/auth-template';
import { SIGN_UP_CONFIG } from '@pages/sign-up/constants/form';
import { Input } from '@components/input';
import { TSignUpData } from '@models/sign-up-data';
import { createUser } from '@service/user';
import { useDebounce } from '@hooks/use-debounce';
import { Button, Modal, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Icon } from '@components/icon';
import * as S from './styles';

export function SignUp() {
  const form = useForm<TSignUpData>(SIGN_UP_CONFIG);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [open, toggleModal] = useToggle(false);
  const [openError, toggleErrorModal] = useToggle(false);

  const urlCompany = searchParams.get('company') || searchParams.get('empresa');

  React.useEffect(() => {
    if (urlCompany) {
      form.setValue('company', urlCompany);
    }
  }, [urlCompany, form]);

  const navigateToLogin = () => {
    navigate('/');
  };

  const mutation = useMutation({
    mutationFn: createUser,
    onError: toggleErrorModal,
    onSuccess: toggleModal,
  });

  const onSubmit = useDebounce(() => {
    form.handleSubmit((data: TSignUpData) => {mutation.mutate(data)})();
  }, 600);

  return (
    <AuthTemplate
      title="Cadastrar"
      buttonText="Cadastrar"
      buttonOnClick={onSubmit}
    >
      <Input
        name="name"
        control={form.control}
        label="Nome"
        variant="outlined"
        fullWidth
        placeholder="João Amaral"
        sx={{ mt: 2 }}
      />
      <Input
        name="company"
        control={form.control}
        label="Empresa"
        variant="outlined"
        fullWidth
        placeholder="Greentech"
        sx={{ mt: 2 }}
        disabled={!!urlCompany}
      />
      <Grid direction="row" display="flex" mt={4}>
        <Input
          name="username"
          control={form.control}
          label="Usuário"
          variant="outlined"
          sx={{ mr: 3 }}
          placeholder="@camaral"
          fullWidth
        />
        <Input
          name="email"
          control={form.control}
          label="E-mail"
          variant="outlined"
          placeholder="carlos.amaral@company.com.br"
          fullWidth
        />
      </Grid>
      <Grid direction="row" display="flex" mt={4} mb={4}>
        <Input
          name="password"
          control={form.control}
          label="Senha"
          variant="outlined"
          sx={{ mr: 3 }}
          placeholder="********"
          type="password"
          fullWidth
        />
        <Input
          name="confirmPassword"
          control={form.control}
          label="Repita a senha"
          variant="outlined"
          placeholder="********"
          type="password"
          fullWidth
        />
      </Grid>
      <Input
        name="role"
        control={form.control}
        label="Cargo"
        variant="outlined"
        placeholder="Picker/Líder/Operador (campo para digitar)"
        fullWidth
      />
      <Modal open={open} onClose={toggleModal}>
        <S.ModalContainer>
          <Box
            maxWidth={200}
            justifyContent="center"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Icon disableTheming name="aprove" alt="aprove icon" width={36} />
            <Typography
              variant="h5"
              color="primary"
              fontWeight={600}
              textAlign="center"
              mt={2}
            >
              Usuário criado com sucesso
            </Typography>
            <S.Button onClick={navigateToLogin}>OK</S.Button>
          </Box>
        </S.ModalContainer>
      </Modal>
      <Modal open={openError} onClose={toggleErrorModal}>
        <S.ModalContainer>
          <Box
            maxWidth={200}
            justifyContent="center"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Icon disableTheming name="error" alt="aprove icon" width={36} />
            <Typography
              variant="h5"
              color="primary"
              fontWeight={600}
              textAlign="center"
              mt={2}
            >
              Erro ao criar usuário ou perfil já existente
            </Typography>
            <S.Button onClick={toggleErrorModal}>OK</S.Button>
          </Box>
        </S.ModalContainer>
      </Modal>
    </AuthTemplate>
  );
}
